import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiUrl } from '../utils/config';
import logger from '../utils/logger';

const ABTestContext = createContext();

// A/B Test configurations
// NOTE: Variants use translation keys to support multi-language
// Variant A = Default (uses standard translation keys)
// Variant B = Alternative texts (uses variant-specific translation keys)
export const AB_TESTS = {
  HERO_HEADLINE: {
    id: 'hero_headline',
    name: 'Hero Headline Test',
    description: 'Test different hero headlines for conversion',
    variants: {
      A: {
        // Variant A uses default translation keys (no override)
        useDefault: true
      },
      B: {
        // Variant B uses alternative translation keys
        b2b: {
          titleKey: 'hero.b2b.titleB',
          subtitleKey: 'hero.b2b.subtitleB'
        },
        b2c: {
          titleKey: 'hero.b2c.titleB',
          subtitleKey: 'hero.b2c.subtitleB'
        }
      }
    }
  },
  CTA_BUTTON: {
    id: 'cta_button',
    name: 'CTA Button Test',
    description: 'Test different CTA button text and styles',
    variants: {
      A: {
        // Variant A uses default CTA text
        useDefault: true,
        b2b: { color: 'blue' },
        b2c: { color: 'emerald' }
      },
      B: {
        b2b: { textKey: 'hero.b2b.ctaB', color: 'indigo' },
        b2c: { textKey: 'hero.b2c.ctaB', color: 'teal' }
      },
      C: {
        b2b: { textKey: 'hero.b2b.ctaC', color: 'purple' },
        b2c: { textKey: 'hero.b2c.ctaC', color: 'cyan' }
      }
    }
  },
  AUDIENCE_SELECTOR: {
    id: 'audience_selector',
    name: 'Audience Selector Test',
    description: 'Test modal vs inline audience selection',
    variants: {
      A: { type: 'modal', description: 'Pop-up modal on first visit' },
      B: { type: 'inline', description: 'Inline selection in hero section' },
      C: { type: 'banner', description: 'Top banner with selection' }
    }
  },
  PRICING_DISPLAY: {
    id: 'pricing_display',
    name: 'Pricing Display Test',
    description: 'Test pricing visibility for B2C services',
    variants: {
      A: { showPrice: true, ctaTextKey: 'shop.addToCart' },
      B: { showPrice: true, ctaTextKey: 'shop.addToCart' },
      C: { showPrice: true, ctaTextKey: 'shop.getStarted', showDiscount: true }
    }
  }
};

// Generate or retrieve user's test variant assignments
const getOrCreateVariantAssignments = () => {
  const stored = localStorage.getItem('coachmeupp_ab_tests');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      logger.warn('Invalid stored A/B test data, regenerating');
    }
  }
  
  // Generate new assignments
  const assignments = {};
  const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  Object.keys(AB_TESTS).forEach(testKey => {
    const test = AB_TESTS[testKey];
    const variantKeys = Object.keys(test.variants);
    // Simple random assignment
    const randomIndex = Math.floor(Math.random() * variantKeys.length);
    assignments[test.id] = {
      variant: variantKeys[randomIndex],
      assignedAt: new Date().toISOString()
    };
  });
  
  const data = { userId, assignments, createdAt: new Date().toISOString() };
  localStorage.setItem('coachmeupp_ab_tests', JSON.stringify(data));
  
  return data;
};

export const ABTestProvider = ({ children }) => {
  const [testData, setTestData] = useState(null);
  const [conversionEvents, setConversionEvents] = useState([]);

  const reportAssignments = useCallback(async (data) => {
    try {
      await fetch(apiUrl('ab-tests/assignments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      logger.error('Failed to report A/B test assignments:', error);
    }
  }, []);

  useEffect(() => {
    const data = getOrCreateVariantAssignments();
    setTestData(data);
    
    // Report assignment to backend
    reportAssignments(data);
  }, [reportAssignments]);

  const getVariant = useCallback((testId) => {
    if (!testData?.assignments?.[testId]) {
      return 'A'; // Default to variant A
    }
    return testData.assignments[testId].variant;
  }, [testData]);

  const getTestConfig = useCallback((testKey, audience = null) => {
    const test = AB_TESTS[testKey];
    if (!test) return null;
    
    const variant = getVariant(test.id);
    const variantConfig = test.variants[variant];
    
    // If variant config has audience-specific settings
    if (audience && variantConfig[audience]) {
      return { ...variantConfig[audience], variant };
    }
    
    return { ...variantConfig, variant };
  }, [getVariant]);

  const trackConversion = useCallback(async (testId, eventType, metadata = {}) => {
    if (!testData) return;
    
    const event = {
      userId: testData.userId,
      testId,
      variant: testData.assignments[testId]?.variant || 'A',
      eventType,
      metadata,
      timestamp: new Date().toISOString()
    };
    
    setConversionEvents(prev => [...prev, event]);
    
    // Report to backend
    try {
      await fetch(apiUrl('ab-tests/conversions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event)
      });
    } catch (error) {
      logger.error('Failed to track conversion:', error);
    }
  }, [testData]);

  const trackImpression = useCallback(async (testId) => {
    if (!testData) return;
    
    try {
      await fetch(apiUrl('ab-tests/impressions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: testData.userId,
          testId,
          variant: testData.assignments[testId]?.variant || 'A',
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      logger.error('Failed to track impression:', error);
    }
  }, [testData]);

  // Force a specific variant (for testing/preview)
  const forceVariant = useCallback((testId, variant) => {
    if (!testData) return;
    
    const newData = {
      ...testData,
      assignments: {
        ...testData.assignments,
        [testId]: { variant, assignedAt: new Date().toISOString(), forced: true }
      }
    };
    
    setTestData(newData);
    localStorage.setItem('coachmeupp_ab_tests', JSON.stringify(newData));
  }, [testData]);

  const resetTests = useCallback(() => {
    localStorage.removeItem('coachmeupp_ab_tests');
    const newData = getOrCreateVariantAssignments();
    setTestData(newData);
    reportAssignments(newData);
  }, [reportAssignments]);

  const isLoaded = !!testData;

  const value = useMemo(() => ({
    testData,
    getVariant,
    getTestConfig,
    trackConversion,
    trackImpression,
    forceVariant,
    resetTests,
    AB_TESTS,
    isLoaded
  }), [testData, getVariant, getTestConfig, trackConversion, trackImpression, forceVariant, resetTests, isLoaded]);

  return (
    <ABTestContext.Provider value={value}>
      {children}
    </ABTestContext.Provider>
  );
};

export const useABTest = () => {
  const context = useContext(ABTestContext);
  if (!context) {
    throw new Error('useABTest must be used within an ABTestProvider');
  }
  return context;
};

export default ABTestContext;
